import {
  BuildingOfficeIcon,
  ChartBarIcon,
  CreditCardIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { User, UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { Navbar, Sidebar, StorageIcon } from '@qse/ui'
import { type FC, type ReactNode } from 'react'
import { Outlet } from 'react-router-dom'

import { AppRoutesList } from '@/router/routes'

interface DashboardLayoutProps {
  children?: ReactNode
}

/** Super-Admin portal shell — SUPER_ADMIN navigation only. */
const DashboardLayout: FC<DashboardLayoutProps> = () => {
  const { user } = useUserStore()
  const userType = user?.userRole || ''
  const isSuperAdmin = userType === UserRoleEnum.SUPER_ADMIN
  const isResellerAdmin = userType === UserRoleEnum.RESELLER_ADMIN

  const adminMenuItems = [
    ...(isSuperAdmin
      ? [
          {
            id: 'resellers',
            label: 'Reseller Management',
            icon: StorageIcon,
            redirectUrl: AppRoutesList.reseller,
          },
        ]
      : []),
    ...(isSuperAdmin || isResellerAdmin
      ? [
          {
            id: 'tenants',
            label: 'Tenant Management',
            icon: BuildingOfficeIcon,
            redirectUrl: AppRoutesList.tenants,
          },
          {
            id: 'plans',
            label: 'Plan Management',
            icon: CreditCardIcon,
            redirectUrl: AppRoutesList.plan,
          },
          {
            id: 'storageReport',
            label: 'Storage Report',
            icon: ChartBarIcon,
            redirectUrl: AppRoutesList.storageReport,
          },
        ]
      : []),
    {
      id: 'profile',
      label: 'Profile',
      icon: UserIcon,
      redirectUrl: AppRoutesList.profile,
    },
  ]

  const securitySubmenuItems: Array<{
    label: string
    redirectUrl: string
  }> = []

  if (!user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-black">
      <aside className="bg-white dark:bg-[#282828] text-gray-900 dark:text-white">
        <Sidebar
          isOpen={true}
          menuItems={adminMenuItems}
          securitySubmenuItems={securitySubmenuItems}
          setIsOpen={() => {}}
          showSecurityTab={false}
          user={user as User}
        />
      </aside>
      <main className="flex-1 overflow-y-scroll scrollbar-hide bg-gray-50 dark:bg-black relative">
        <Navbar />
        <Outlet />
      </main>
    </div>
  )
}

export default DashboardLayout
